import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import api from '../services/api';
import { useSubscription } from './SubscriptionContext';
import {
  getCachedPhotoUrl,
  setCachedPhotoUrl,
  cleanupExpiredEntries,
} from '../utils/indexedDB';

const PhotoCacheContext = createContext(null);

// In-memory cache TTL: 24 hours (Firebase Storage URLs never expire, so we can cache longer)
const MEMORY_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// IndexedDB cache TTL: 30 days (for persistent cross-session caching)
const INDEXEDDB_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Check if a URL is a Firebase Storage URL (signed or public) vs Google Places URL (temporary)
 */
function isFirebaseStorageUrl(url) {
  if (!url) return false;
  // Check for signed URLs (contain Signature=) or legacy public URLs
  return (url.includes('storage.googleapis.com') && url.includes('Signature=')) ||
         url.includes('firebasestorage.googleapis.com');
}

// Concurrency limit for photo fetching (prevents burst rate limiting)
const MAX_CONCURRENT_FETCHES = 5;
// Delay between batches to avoid triggering rate limits
const BATCH_DELAY_MS = 100;

export function PhotoCacheProvider({ children }) {
  // Use ref to avoid re-renders when cache updates
  const cacheRef = useRef(new Map());
  const pendingRef = useRef(new Map());
  const { isPremium } = useSubscription();

  // Queue management for throttled fetching
  const queueRef = useRef([]);
  const activeCountRef = useRef(0);
  const processingRef = useRef(false);

  // Clean up expired IndexedDB entries on mount
  useEffect(() => {
    cleanupExpiredEntries().catch(() => {
      // Silently fail if cleanup fails
    });
  }, []);

  const isMemoryCacheExpired = useCallback((timestamp) => {
    return Date.now() - timestamp > MEMORY_CACHE_TTL_MS;
  }, []);

  /**
   * Process the fetch queue with concurrency limiting
   * Ensures we don't exceed MAX_CONCURRENT_FETCHES at a time
   */
  const processQueue = useCallback(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    const processNext = async () => {
      // Process items while we have capacity and items in queue
      while (activeCountRef.current < MAX_CONCURRENT_FETCHES && queueRef.current.length > 0) {
        const item = queueRef.current.shift();
        if (!item) break;

        const { placeId, resolve, reject } = item;
        activeCountRef.current++;

        // Execute the actual fetch
        (async () => {
          try {
            const response = await api.getPlacePhoto(placeId);
            const url = response.data?.photoUrl;
            if (!url) {
              // No photo available - cache null to avoid repeated requests
              cacheRef.current.set(placeId, {
                url: null,
                timestamp: Date.now(),
              });
              resolve(null);
            } else {
              // Store in memory cache
              cacheRef.current.set(placeId, {
                url,
                timestamp: Date.now(),
              });
              // Persist to IndexedDB for cross-session caching
              setCachedPhotoUrl(placeId, url).catch(() => {});
              resolve(url);
            }
          } catch (err) {
            // Handle 404 silently - it's expected for some places
            if (err.status === 404 || err.message?.includes('No photo available') || err.message?.includes('404')) {
              cacheRef.current.set(placeId, {
                url: null,
                timestamp: Date.now(),
              });
              resolve(null);
            } else if (err.status === 429) {
              // Rate limited - requeue with delay
              setTimeout(() => {
                queueRef.current.push({ placeId, resolve, reject });
                processNext();
              }, 1000);
            } else {
              reject(err);
            }
          } finally {
            activeCountRef.current--;
            pendingRef.current.delete(placeId);
          }
        })();

        // Small delay between starting fetches to spread them out
        if (queueRef.current.length > 0) {
          await new Promise(r => setTimeout(r, BATCH_DELAY_MS));
        }
      }

      processingRef.current = false;
    };

    processNext();
  }, []);

  /**
   * Get photo URL for a place, using cached data when available.
   * Priority:
   * 1. In-memory cache (valid for 24 hours)
   * 2. IndexedDB persistent cache (valid for 30 days)
   * 3. Stored Firebase Storage URL (never expires)
   * 4. For premium users only: Fetch from backend (which gets from Google and stores in Firebase Storage)
   *
   * IMPORTANT: Free users will NOT trigger API calls. They can only see photos that were
   * already fetched by a premium user (stored in Firebase Storage).
   *
   * @param {string} placeId - Google Place ID
   * @param {Object} options - Options object
   * @param {string} options.storedPhotoUrl - Previously stored photo URL
   * @param {string} options.photoUrlSavedAt - When the stored URL was saved (unused for Firebase URLs)
   * @returns {Promise<string>} - Photo URL (Firebase Storage URL)
   */
  const getPhotoUrl = useCallback(async (placeId, options = {}) => {
    const { storedPhotoUrl } = options;

    if (!placeId) {
      return Promise.reject(new Error('Place ID is required'));
    }

    const cacheKey = placeId;

    // 1. Check in-memory cache first (fastest)
    const cached = cacheRef.current.get(cacheKey);
    if (cached && !isMemoryCacheExpired(cached.timestamp)) {
      return cached.url;
    }

    // 2. Check IndexedDB persistent cache (survives browser restarts)
    try {
      const indexedDBUrl = await getCachedPhotoUrl(placeId, INDEXEDDB_CACHE_TTL_MS);
      if (indexedDBUrl) {
        // Promote to memory cache for faster subsequent access
        cacheRef.current.set(cacheKey, {
          url: indexedDBUrl,
          timestamp: Date.now(),
        });
        return indexedDBUrl;
      }
    } catch {
      // IndexedDB not available, continue to next cache level
    }

    // 3. If we have a Firebase Storage URL, use it (never expires)
    if (storedPhotoUrl && isFirebaseStorageUrl(storedPhotoUrl)) {
      cacheRef.current.set(cacheKey, {
        url: storedPhotoUrl,
        timestamp: Date.now(),
      });
      // Also persist to IndexedDB for cross-session caching
      setCachedPhotoUrl(placeId, storedPhotoUrl).catch(() => {});
      return storedPhotoUrl;
    }

    // 4. For free users, don't make API calls - return null
    // Photos are only fetched for premium users via enrichPlace
    if (!isPremium) {
      return null;
    }

    // 5. Check if there's already a pending request for this placeId
    const pending = pendingRef.current.get(cacheKey);
    if (pending) {
      return pending;
    }

    // 6. Premium only: Add to fetch queue (throttled to prevent rate limiting)
    const fetchPromise = new Promise((resolve, reject) => {
      queueRef.current.push({ placeId, resolve, reject });
      processQueue();
    });

    pendingRef.current.set(cacheKey, fetchPromise);
    return fetchPromise;
  }, [isMemoryCacheExpired, isPremium, processQueue]);

  /**
   * Legacy method - always fetches fresh (for backwards compatibility)
   * Use getPhotoUrl with stored data when possible
   */
  const getFreshPhotoUrl = useCallback(async (placeId) => {
    return getPhotoUrl(placeId, {});
  }, [getPhotoUrl]);

  /**
   * Check if a stored photo URL needs refresh
   * With Firebase Storage, URLs never expire, so only non-Firebase URLs need refresh
   */
  const needsPhotoRefresh = useCallback((photoUrl) => {
    if (!photoUrl) return true;
    return !isFirebaseStorageUrl(photoUrl);
  }, []);

  /**
   * Force refresh a photo URL (used when cached URL returns 403/expired)
   * Clears memory cache and fetches from backend (which stores in Firebase Storage)
   * Premium only - free users cannot trigger photo fetches
   * @param {string} placeId - Google Place ID
   * @returns {Promise<string>} - Fresh photo URL (Firebase Storage)
   */
  const refreshPhotoUrl = useCallback(async (placeId) => {
    if (!placeId) {
      return Promise.reject(new Error('Place ID is required'));
    }

    // Free users cannot trigger photo fetches
    if (!isPremium) {
      return null;
    }

    const cacheKey = placeId;

    // Clear from memory cache
    cacheRef.current.delete(cacheKey);

    // Check if there's already a pending refresh for this placeId
    const pending = pendingRef.current.get(cacheKey);
    if (pending) {
      return pending;
    }

    // Add to fetch queue (throttled to prevent rate limiting)
    const fetchPromise = new Promise((resolve, reject) => {
      queueRef.current.push({ placeId, resolve, reject });
      processQueue();
    });

    pendingRef.current.set(cacheKey, fetchPromise);
    return fetchPromise;
  }, [isPremium, processQueue]);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const getCacheStats = useCallback(() => {
    const cache = cacheRef.current;
    const pending = pendingRef.current;
    let validEntries = 0;
    let expiredEntries = 0;

    cache.forEach((entry) => {
      if (isMemoryCacheExpired(entry.timestamp)) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    });

    return {
      totalCached: cache.size,
      validEntries,
      expiredEntries,
      pendingRequests: pending.size,
      queueSize: queueRef.current.length,
      activeRequests: activeCountRef.current,
    };
  }, [isMemoryCacheExpired]);

  const value = {
    getPhotoUrl,
    getFreshPhotoUrl, // Legacy support
    refreshPhotoUrl,  // Force refresh when URL is expired (403)
    needsPhotoRefresh,
    clearCache,
    getCacheStats,
  };

  return (
    <PhotoCacheContext.Provider value={value}>
      {children}
    </PhotoCacheContext.Provider>
  );
}

export function usePhotoCache() {
  const context = useContext(PhotoCacheContext);
  if (!context) {
    throw new Error('usePhotoCache must be used within a PhotoCacheProvider');
  }
  return context;
}

export default PhotoCacheContext;
